import { useState } from 'react';
import { Menu, Avatar, Dropdown } from 'antd';
import {
    InboxOutlined,
    StarOutlined,
    FolderOutlined,
    FileOutlined,
    DeleteOutlined,
    PlusOutlined,
    UserOutlined,
    EditOutlined,
    MoreOutlined,
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext.jsx';
import { useNotes } from '../../contexts/NotesContext.jsx';
import { useFolders } from '../../contexts/FoldersContext.jsx';
import { useTags } from '../../contexts/TagsContext.jsx';
import { useOrganization } from '../../contexts/OrganizationContext.jsx';
import { useTheme } from '../../contexts/ThemeContext.jsx';
import Icon from '../common/Icon';
import './Sidebar.css';

export default function Sidebar({
    currentView,
    setCurrentView,
    onClose,
    isCollapsed,
    onCreateFolder,
    onCreateTag,
    onEditFolder,
    onEditTag,
    onDeleteFolder,
    onDeleteTag
}) {
    const { user, isPro, planName } = useAuth();
    const { allNotes } = useNotes();
    const { folders, selectedFolderId, selectFolder } = useFolders();
    const { tags, selectedTagIds, toggleTagFilter } = useTags();
    const { filters, updateFilters } = useOrganization();
    const { isDark } = useTheme();

    // Track open submenus
    const [openKeys, setOpenKeys] = useState(['folders-submenu', 'tags-submenu']);

    // Calculate counts
    const counts = {
        all: allNotes.filter(n => !n.isDeleted).length,
        favorites: allNotes.filter(n => n.isFavorite && !n.isDeleted && !n.isArchived).length,
        archived: allNotes.filter(n => n.isArchived && !n.isDeleted).length,
        deleted: allNotes.filter(n => n.isDeleted).length,
    };

    // Get folder context menu items
    const getFolderMenuItems = (folder) => [
        {
            key: 'edit',
            icon: <EditOutlined />,
            label: 'Edit Folder',
            onClick: (e) => {
                e.domEvent?.stopPropagation();
                onEditFolder?.(folder);
            }
        },
        {
            key: 'delete',
            icon: <DeleteOutlined />,
            label: 'Delete Folder',
            danger: true,
            onClick: (e) => {
                e.domEvent?.stopPropagation();
                onDeleteFolder?.(folder);
            }
        }
    ];

    // Get tag context menu items
    const getTagMenuItems = (tag) => [
        {
            key: 'edit',
            icon: <EditOutlined />,
            label: 'Edit Tag',
            onClick: (e) => {
                e.domEvent?.stopPropagation();
                onEditTag?.(tag);
            }
        },
        {
            key: 'delete',
            icon: <DeleteOutlined />,
            label: 'Delete Tag',
            danger: true,
            onClick: (e) => {
                e.domEvent?.stopPropagation();
                onDeleteTag?.(tag);
            }
        }
    ];

    // Build menu items with submenus for folders and tags
    const menuItems = [
        {
            key: 'views',
            type: 'group',
            label: !isCollapsed && 'VIEWS',
            children: [
                {
                    key: 'notes',
                    icon: <InboxOutlined />,
                    label: 'All Notes' + (!isCollapsed ? ' (' + counts.all + ')' : ''),
                    onClick: () => {
                        setCurrentView('notes');
                        updateFilters({ status: 'active' });
                        selectFolder(null);
                        onClose();
                    }
                },
                {
                    key: 'favorites',
                    icon: <StarOutlined />,
                    label: 'Favorites' + (!isCollapsed ? ' (' + counts.favorites + ')' : ''),
                    onClick: () => {
                        setCurrentView('notes');
                        updateFilters({ status: 'favorites' });
                        selectFolder(null);
                        onClose();
                    }
                },
                {
                    key: 'archived',
                    icon: <FileOutlined />,
                    label: 'Archived' + (!isCollapsed ? ' (' + counts.archived + ')' : ''),
                    onClick: () => {
                        setCurrentView('notes');
                        updateFilters({ status: 'archived' });
                        selectFolder(null);
                        onClose();
                    }
                },
                {
                    key: 'deleted',
                    icon: <DeleteOutlined />,
                    label: 'Deleted' + (!isCollapsed ? ' (' + counts.deleted + ')' : ''),
                    onClick: () => {
                        setCurrentView('notes');
                        updateFilters({ status: 'deleted' });
                        selectFolder(null);
                        onClose();
                    }
                },
            ],
        },
        // Folders as collapsible submenu
        {
            key: 'folders-submenu',
            icon: <FolderOutlined />,
            label: (
                <div className="submenu-header">
                    <span>Folders</span>
                    {!isCollapsed && onCreateFolder && (
                        <button
                            className="add-btn-inline"
                            onClick={(e) => {
                                e.stopPropagation();
                                onCreateFolder();
                            }}
                        >
                            <PlusOutlined />
                        </button>
                    )}
                </div>
            ),
            children: folders.length > 0 ? folders.map(folder => ({
                key: 'folder-' + folder.id,
                icon: <Icon name={folder.icon || 'Folder'} size="sm" style={{ color: folder.color }} />,
                label: (
                    <div className="menu-item-with-actions">
                        <span className="item-name">{folder.name}</span>
                        {!isCollapsed && (
                            <Dropdown
                                menu={{ items: getFolderMenuItems(folder) }}
                                trigger={['click']}
                                placement="bottomRight"
                            >
                                <button
                                    className="item-actions-btn"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <MoreOutlined />
                                </button>
                            </Dropdown>
                        )}
                    </div>
                ),
                onClick: () => {
                    selectFolder(folder.id);
                    setCurrentView('notes');
                    onClose();
                }
            })) : [{
                key: 'no-folders',
                label: <span className="empty-hint">No folders yet</span>,
                disabled: true
            }]
        },
        // Tags as collapsible submenu
        {
            key: 'tags-submenu',
            icon: <Icon name="Tag" size="sm" />,
            label: (
                <div className="submenu-header">
                    <span>Tags</span>
                    {!isCollapsed && onCreateTag && (
                        <button
                            className="add-btn-inline"
                            onClick={(e) => {
                                e.stopPropagation();
                                onCreateTag();
                            }}
                        >
                            <PlusOutlined />
                        </button>
                    )}
                </div>
            ),
            children: tags.length > 0 ? tags.map(tag => ({
                key: 'tag-' + tag.id,
                icon: <span style={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    backgroundColor: tag.color,
                    display: 'inline-block',
                    flexShrink: 0
                }} />,
                label: (
                    <div className="menu-item-with-actions">
                        <span className="item-name">{tag.name}</span>
                        {!isCollapsed && (
                            <Dropdown
                                menu={{ items: getTagMenuItems(tag) }}
                                trigger={['click']}
                                placement="bottomRight"
                            >
                                <button
                                    className="item-actions-btn"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <MoreOutlined />
                                </button>
                            </Dropdown>
                        )}
                    </div>
                ),
                onClick: () => {
                    toggleTagFilter(tag.id);
                    setCurrentView('notes');
                    onClose();
                }
            })) : [{
                key: 'no-tags',
                label: <span className="empty-hint">No tags yet</span>,
                disabled: true
            }]
        },
    ];

    // Selected keys
    const getSelectedKeys = () => {
        if (currentView === 'settings') return ['settings'];
        if (currentView === 'account') return ['account'];
        if (selectedFolderId) return ['folder-' + selectedFolderId];
        if (selectedTagIds.length > 0) return selectedTagIds.map(id => 'tag-' + id);

        // Based on filter status
        if (filters.status === 'favorites') return ['favorites'];
        if (filters.status === 'archived') return ['archived'];
        if (filters.status === 'deleted') return ['deleted'];
        return ['notes'];
    };

    const handleOpenChange = (keys) => {
        setOpenKeys(keys);
    };

    return (
        <div className="sidebar-content">
            {/* Logo */}
            <div className="sidebar-logo">
                <Icon name="StickyNote" size="lg" />
                {!isCollapsed && <span className="logo-text">Page Notes</span>}
            </div>

            {/* Menu - Wrapped in scrollable container */}
            <div className="sidebar-menu-wrapper">
                <Menu
                    mode="inline"
                    theme={isDark ? "dark" : "light"}
                    selectedKeys={getSelectedKeys()}
                    openKeys={isCollapsed ? [] : openKeys}
                    onOpenChange={handleOpenChange}
                    items={menuItems}
                    inlineCollapsed={isCollapsed}
                    className="sidebar-menu"
                />
            </div>

            {/* User Section */}
            <div className="sidebar-user">
                <div className="sidebar-user__avatar-wrap">
                    <Avatar
                        size={isCollapsed ? 32 : 40}
                        src={user?.photoURL}
                        icon={<UserOutlined />}
                    />
                    {isPro && (
                        <span className="sidebar-pro-badge" title="Pro User">PRO</span>
                    )}
                </div>
                {!isCollapsed && (
                    <div className="user-info">
                        <div className="user-name">
                            {user?.displayName || user?.email}
                        </div>
                        <div className="user-plan">
                            <span className={`plan-indicator ${isPro ? 'plan-indicator--pro' : 'plan-indicator--free'}`}>
                                {planName}
                            </span>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}